import { useState, useEffect } from 'react';
import { RefreshCw, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { fetchLogs, GenerationLog, MODE_LABELS } from '../../../lib/prompt-admin-api';
import { GenerationPreviewModal } from './GenerationPreviewModal';

export function LogsTab() {
    const [logs, setLogs] = useState<GenerationLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [modeFilter, setModeFilter] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [selectedLog, setSelectedLog] = useState<GenerationLog | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    const handleOpenPreview = (log: GenerationLog) => {
        setSelectedLog(log);
        setPreviewOpen(true);
    };

    useEffect(() => {
        loadLogs();
    }, [page, modeFilter, statusFilter]);

    const loadLogs = async () => {
        try {
            setLoading(true);
            const data = await fetchLogs(page, 30, {
                mode: modeFilter || undefined,
                status: statusFilter || undefined,
            });
            setLogs(data.logs);
            setTotalPages(data.pagination.totalPages);
        } catch (error) {
            toast.error('Nie udało się załadować logów');
        } finally {
            setLoading(false);
        }
    };

    if (loading && logs.length === 0) {
        return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin" /></div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center flex-wrap gap-4">
                <h2 className="text-xl font-semibold">Logi generacji</h2>
                <div className="flex gap-2">
                    <select
                        value={modeFilter}
                        onChange={(e) => { setModeFilter(e.target.value); setPage(1); }}
                        className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
                        title="Filtruj po trybie"
                    >
                        <option value="">Wszystkie tryby</option>
                        <option value="enhance">Poprawa jakości</option>
                        <option value="staging">Staging</option>
                        <option value="renovation">Renowacja</option>
                    </select>
                    <select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                        className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
                        title="Filtruj po statusie"
                    >
                        <option value="">Wszystkie statusy</option>
                        <option value="success">Sukces</option>
                        <option value="error">Błąd</option>
                        <option value="pending">W trakcie</option>
                    </select>
                    <Button variant="outline" size="icon" onClick={loadLogs}>
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            <div className="bg-card border border-border rounded-xl overflow-x-auto">
                <table className="w-full min-w-[800px]">
                    <thead className="bg-muted/50">
                        <tr>
                            <th className="text-left p-3 text-sm font-medium">Data</th>
                            <th className="text-left p-3 text-sm font-medium">Użytkownik</th>
                            <th className="text-left p-3 text-sm font-medium">Tryb</th>
                            <th className="text-center p-3 text-sm font-medium">Status</th>
                            <th className="text-center p-3 text-sm font-medium">Podgląd</th>
                            <th className="text-center p-3 text-sm font-medium">Akcje</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map(log => (
                            <tr key={log.id} className="border-t border-border">
                                <td className="p-3 text-sm text-muted-foreground">
                                    {new Date(log.created_at).toLocaleString('pl-PL')}
                                </td>
                                <td className="p-3 text-sm">{log.profiles?.email || log.user_id.slice(0, 8)}</td>
                                <td className="p-3 text-sm">{MODE_LABELS[log.mode] || log.mode}</td>
                                <td className="p-3 text-center">
                                    <span className={`px-2 py-0.5 rounded text-xs ${log.status === 'success' ? 'bg-green-500/20 text-green-500' :
                                        log.status === 'error' ? 'bg-red-500/20 text-red-500' :
                                            'bg-yellow-500/20 text-yellow-500'
                                        }`}>
                                        {log.status}
                                    </span>
                                </td>
                                <td className="p-3">
                                    <div className="flex gap-2 items-center justify-center">
                                        {/* Input thumbnail */}
                                        {log.input_image_url && (
                                            <div className="relative group">
                                                <img
                                                    src={log.input_image_url}
                                                    alt="Input"
                                                    className="w-16 h-16 object-cover rounded border border-border cursor-default hover:border-primary transition-colors"
                                                />
                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center pointer-events-none">
                                                    <Eye className="w-5 h-5 text-white" />
                                                </div>
                                            </div>
                                        )}

                                        {/* Output thumbnail (pierwszy wariant) */}
                                        {log.output_variants?.[0] && (
                                            <div className="relative group">
                                                <img
                                                    src={log.output_variants[0].url}
                                                    alt="Output"
                                                    className="w-16 h-16 object-cover rounded border-2 border-primary cursor-default hover:scale-105 transition-transform"
                                                />
                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center pointer-events-none">
                                                    <Eye className="w-5 h-5 text-white" />
                                                </div>
                                                {log.output_variants.length > 1 && (
                                                    <div className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center pointer-events-none">
                                                        {log.output_variants.length}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Fallback jeśli brak obrazów */}
                                        {!log.input_image_url && !log.output_variants?.[0] && (
                                            <div className="w-16 h-16 bg-muted rounded flex items-center justify-center text-muted-foreground text-xs">
                                                Brak
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td className="p-3 text-center">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleOpenPreview(log)}
                                        className="gap-1"
                                    >
                                        <Eye className="w-4 h-4" />
                                        Szczegóły
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {logs.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                    Brak logów do wyświetlenia
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex justify-center gap-2">
                    <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                        Poprzednia
                    </Button>
                    <span className="px-4 py-2 text-sm">Strona {page} z {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                        Następna
                    </Button>
                </div>
            )}

            {/* Preview Modal */}
            {selectedLog && (
                <GenerationPreviewModal
                    log={selectedLog}
                    isOpen={previewOpen}
                    onOpenChange={(open) => setPreviewOpen(open)}
                />
            )}
        </div>
    );
}
